import React, { useState, useEffect } from 'react';
import { Users, Shield, Search, RefreshCw, Check, AlertCircle, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { getAllRoles, getRoleLabel, getRoleColorClasses } from '../utils/roleConfig';
import { getUserRoles, hasRole } from '../utils/roleUtils';
import { filterUsersByRole, filterUsersBySearch, sortUsersByName, getUserStatistics } from '../utils/userUtils';

const UserRoleManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [updating, setUpdating] = useState(null);

  // Get available roles from config
  const availableRoles = getAllRoles();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      const usersRes = await fetch(`${BACKEND_URL}/api/users`);
      
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(sortUsersByName(usersData.users || []));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const handleRolesUpdate = async (userId, newRoles, userName) => {
    setUpdating(userId);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${BACKEND_URL}/api/users/${userId}/roles`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          roles: newRoles
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update roles');
      }
      
      // Update local state
      setUsers(users.map(u => 
        u.id === userId ? { ...u, roles: newRoles, role: newRoles[0] } : u
      ));
      
      const roleLabels = newRoles.map(r => getRoleLabel(r)).join(', ');
      toast.success(`Updated ${userName}'s roles to: ${roleLabels}`);
      
    } catch (error) {
      console.error('Error updating roles:', error);
      toast.error('Failed to update roles');
    } finally {
      setUpdating(null);
    }
  };

  const handleRoleToggle = (userId, roleValue, userName) => {
    const user = users.find(u => u.id === userId);
    const currentRoles = getUserRoles(user);
    
    let newRoles;
    if (hasRole(user, roleValue)) {
      // Remove role
      newRoles = currentRoles.filter(r => r !== roleValue);
      if (newRoles.length === 0) {
        toast.error('User must have at least one role');
        return;
      }
    } else {
      // Add role
      newRoles = [...currentRoles, roleValue];
    }
    
    handleRolesUpdate(userId, newRoles, userName);
  };

  // Apply filters
  let filteredUsers = users;
  filteredUsers = filterUsersByRole(filteredUsers, roleFilter);
  filteredUsers = filterUsersBySearch(filteredUsers, searchTerm);

  // Get statistics
  const stats = getUserStatistics(users);

  return (
    <div className="space-y-6" data-testid="user-role-management">
      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Shield className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">User Role Management</h2>
              <p className="text-sm text-gray-600 mt-1">
                Manage user roles and permissions across the platform
              </p>
            </div>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="refresh-button"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {availableRoles.map(role => (
            <div
              key={role.value}
              className={`p-4 rounded-lg border ${getRoleColorClasses(role.value)}`}
              data-testid={`role-count-${role.value}`}
            >
              <div className="text-2xl font-bold">{stats.byRole[role.value] || 0}</div>
              <div className="text-xs font-medium mt-1">{role.label}</div>
            </div>
          ))}
        </div>

        {/* Overall Stats */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between text-sm">
            <div>
              <span className="text-gray-600">Total Users: </span>
              <span className="font-semibold text-gray-900">{stats.total}</span>
            </div>
            <div>
              <span className="text-gray-600">Active: </span>
              <span className="font-semibold text-green-700">{stats.active}</span>
            </div>
            <div>
              <span className="text-gray-600">Team Leads: </span>
              <span className="font-semibold text-blue-700">{stats.teamLeads}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, username, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="search-users-input"
            />
          </div>

          {/* Role Filter */}
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-400" />
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="role-filter"
            >
              <option value="all">All Roles ({users.length})</option>
              {availableRoles.map(role => (
                <option key={role.value} value={role.value}>
                  {role.label} ({stats.byRole[role.value] || 0})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Active Filters Display */}
        {(searchTerm || roleFilter !== 'all') && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-600">Active filters:</span>
              {searchTerm && (
                <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded border border-blue-200">
                  Search: "{searchTerm}"
                </span>
              )}
              {roleFilter !== 'all' && (
                <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded border border-purple-200">
                  Role: {getRoleLabel(roleFilter)}
                </span>
              )}
              <button
                onClick={() => {
                  setSearchTerm('');
                  setRoleFilter('all');
                }}
                className="ml-auto text-blue-600 hover:text-blue-700 font-medium"
              >
                Clear all
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading users...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-12 text-center">
            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 font-medium">No users found</p>
            <p className="text-sm text-gray-500 mt-1">
              {searchTerm || roleFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'No users available'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current Roles
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assign Roles
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Department
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => {
                  const userRoles = getUserRoles(user);
                  const userName = user.full_name || user.username;
                  
                  return (
                    <tr 
                      key={user.id} 
                      className="hover:bg-gray-50 transition-colors"
                      data-testid={`user-row-${user.id}`}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-blue-100 rounded-full">
                            <Users className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {userName}
                            </div>
                            <div className="text-xs text-gray-500">
                              {user.is_active !== false ? (
                                <span className="text-green-600">● Active</span>
                              ) : (
                                <span className="text-gray-400">● Inactive</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {user.email || 'N/A'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {userRoles.map((roleValue, idx) => (
                            <span 
                              key={idx}
                              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getRoleColorClasses(roleValue)}`}
                            >
                              {getRoleLabel(roleValue)}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-2">
                          {availableRoles.map(role => {
                            const isChecked = hasRole(user, role.value);
                            return (
                              <label 
                                key={role.value}
                                className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors"
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => handleRoleToggle(user.id, role.value, userName)}
                                  disabled={updating === user.id}
                                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                  data-testid={`role-checkbox-${user.id}-${role.value}`}
                                />
                                <span className={`text-xs font-medium ${getRoleColorClasses(role.value).split(' ')[1]}`}>
                                  {role.label}
                                </span>
                              </label>
                            );
                          })}
                          {updating === user.id && (
                            <div className="flex items-center gap-2 text-blue-600 text-xs mt-2">
                              <RefreshCw className="h-3 w-3 animate-spin" />
                              Updating...
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {user.department || 'N/A'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Footer Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Check className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-medium">Role Management Tips:</p>
            <ul className="mt-2 space-y-1 text-blue-800">
              <li>• Changes take effect immediately</li>
              <li>• Users will see updated dashboards on next login/refresh</li>
              <li>• Lead roles can manage teams and have additional permissions</li>
              <li>• Supervisors have visibility across multiple teams</li>
              <li>• Account Managers can manage client relationships</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserRoleManagement;
